import { Doc } from '@dadohr/shared/domainTypes/document';
import { DocuSignIntegrationConfig } from '@dadohr/shared/domainTypes/integrations/docusign';
import styled from '@emotion/styled';
import { Button, Dialog, DialogContent } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import * as ENVS from '../../../../../../env.json';
import {
  useTranslation,
  useUnsafeTranslation
} from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { PageOAuthRedirectProps } from '../../../../../OAuth/pages/Redirect';
import { IntegrationData } from '../../components/IntegrationData';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties,
  IntegrationSetupDialogWithConfigProperties,
  masked
} from '../common';
import { OAuthSetupDialogV2 } from '../oauth';
import { removeDocuSignConfig, useDocuSignConfig } from './service';

const NAME = 'Docusign';

export const DOCUSIGN_OAUTH_REDIRECT_PROPS: PageOAuthRedirectProps = {
  integrationName: NAME,
  cloudFn: 'integrations_docusign-exchangeCodeAndStoreCredentials'
};

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const ExistingSetupDialog = ({
  config,
  open,
  onClose
}: {
  config: Doc<DocuSignIntegrationConfig>;
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useUnsafeTranslation();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <IntegrationData>
          <strong>{t('org.integrations.common.email')}</strong>
          <div>{config.data.email || ''}</div>
          <strong>{t('Account ID')}</strong>
          <div>{config.data.accountId || ''}</div>
          <strong>{t('Base URI')}</strong>
          <div>{config.data.baseUri || ''}</div>
          <strong>{t('org.integrations.common.accessToken')}</strong>
          <div>{masked(config.data.accessToken)}</div>
          <strong>{t('org.integrations.common.refreshToken')}</strong>
          <div>{masked(config.data.refreshToken)}</div>
          <strong>{t('Webhook Username')}</strong>
          <div>{config.data.webhook?.username}</div>
          <strong>{t('Webhook Password')}</strong>
          <div>{config.data.webhook?.password}</div>
          <strong>{t('Webhook URL')}</strong>
          <div>{ENVS.docusign.webhookUrl}</div>
        </IntegrationData>
      </Content>
      <DialogActionsWithSlots
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={() => removeDocuSignConfig(config.data.orgId)}
            pending={`${t('Removing')}...`}
          >
            {t('Remove')}
          </ButtonWithPromise>
        }
        right={
          <Button color="primary" onClick={onClose}>
            {t('Close')}
          </Button>
        }
      />
    </Dialog>
  );
};

const SetupDialogV2 = ({
  config,
  open,
  onClose
}: IntegrationSetupDialogWithConfigProperties<DocuSignIntegrationConfig>) => {
  const { t } = useTranslation();
  const isSetup = !!(
    config?.data.accessToken &&
    config?.data.refreshToken &&
    config?.data.accountId &&
    config?.data.baseUri
  );

  if (!config) {
    return null;
  }

  if (isSetup) {
    return (
      <ExistingSetupDialog config={config} open={open} onClose={onClose} />
    );
  }

  return (
    <OAuthSetupDialogV2
      orgId={config.data.orgId}
      open={open}
      onClose={onClose}
      name={NAME}
      oauthCf={'docusign_oauthGetAuthorizeUrl'}
    >
      <Alert severity="info" icon={false}>
        {t('org.integrations.docusign.setupInfo.p1')}
      </Alert>
    </OAuthSetupDialogV2>
  );
};

export const DocuSignSettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { id: userId, org } = useCurrentUser();
  const [config] = useDocuSignConfig(org.id, userId);
  const cfg = config || null;

  return (
    <>
      {
        <IntegrationConfigurationButton
          name={NAME}
          functionalities={functionalities}
          logoUrl="/images/integrations-logos/docusign.svg"
          active={!!cfg?.data.active}
          interactive={interactive}
          renderSetupDialog={({
            open,
            onClose,
            onFailure,
            onSuccessfulIntegration
          }: IntegrationSetupDialogProperties) => (
            <SetupDialogV2
              config={cfg}
              open={open}
              onClose={onClose}
              onFailure={onFailure}
              onSuccessfulIntegration={onSuccessfulIntegration}
            />
          )}
        />
      }
    </>
  );
};
